const MAX_DIMENSION = 512;
const WEBP_QUALITY = 0.85;

export interface ResizedImage {
  blob: Blob;
  fileName: string;
}

/**
 * Decodes the file, crops it to a centered square, scales the square down to
 * at most MAX_DIMENSION on a side, and re-encodes as WebP.
 */
export async function resizeImageToWebP(file: File): Promise<ResizedImage> {
  const bitmap = await createImageBitmap(file);
  try {
    const sourceMin = Math.min(bitmap.width, bitmap.height);
    const target = Math.min(MAX_DIMENSION, sourceMin);
    const sx = (bitmap.width - sourceMin) / 2;
    const sy = (bitmap.height - sourceMin) / 2;

    const useOffscreen = typeof OffscreenCanvas !== 'undefined';
    const canvas: HTMLCanvasElement | OffscreenCanvas = useOffscreen
      ? new OffscreenCanvas(target, target)
      : Object.assign(document.createElement('canvas'), { width: target, height: target });

    const ctx = canvas.getContext('2d') as
      | CanvasRenderingContext2D
      | OffscreenCanvasRenderingContext2D
      | null;
    if (!ctx) {
      throw new Error('Could not get 2D canvas context');
    }

    ctx.drawImage(bitmap, sx, sy, sourceMin, sourceMin, 0, 0, target, target);

    const blob =
      canvas instanceof OffscreenCanvas
        ? await canvas.convertToBlob({ type: 'image/webp', quality: WEBP_QUALITY })
        : await new Promise<Blob>((resolve, reject) => {
            canvas.toBlob(
              (result) => (result ? resolve(result) : reject(new Error('WebP encode failed'))),
              'image/webp',
              WEBP_QUALITY,
            );
          });

    const baseName = file.name.replace(/\.[^.]+$/, '') || 'avatar';
    return { blob, fileName: `${baseName}.webp` };
  } finally {
    bitmap.close();
  }
}
